
import Vue from "vue";
import { Component, Prop, Inject } from "vue-property-decorator";

import InputWrapper from "./InputWrapper.vue";
import HoverEnabler from "./HoverEnabler.vue";
import DynamicDataChooser from "./DynamicData/DynamicDataChooser.vue";
import ControlBreakpointChooser from "./ControlBreakpointChooser/ControlBreakpointChooser.vue";
import { uiStore, configStore } from "@/store/store";
import { ControlsTreeContext, Control } from "@/types/controls";
import { isControlValueEmpty, valuePresent } from "@/lib/controls/values";
import IndicatorLight from "./IndicatorLight.vue";
import { addHoverToPath } from "@/lib/controls/utils";
import { BASE_BREAKPOINT_ID } from "@/lib/breakpoints/breakpoints";
import { DynamicFieldOutput } from "@/types/dynamic-data";
import { getDynamicMetaPath } from "@/lib/dynamic-data/utils";
import { DynamicFieldReturnType } from "@/types/dynamic-property-path";
import { isObjectExcludingArray } from "@/lib/canvas/flatten-properties";
import { breakdanceUndo } from "@/lib/undo/undo";
import InlineAi from "@/components/AI/InlineAI.vue";

@Component({
  components: {
    InputWrapper,
    ControlBreakpointChooser,
    DynamicDataChooser,
    HoverEnabler,
    IndicatorLight,
    InlineAi
  }
})
export default class Control_ extends Vue {
  @Prop()
  control!: Control;

  @Prop()
  path!: string;

  @Prop({
    default: ""
  })
  prependLabel!: string;

  @Inject("context") readonly context!: ControlsTreeContext;

  get stylesPresent() {
    return (path: string) => valuePresent(path, this.control, this.context);
  }

  get classList() {
    return {
      "breakdance-control-wrapper": true,
      "breakdance-control-wrapper-inline":
        this.control.options.layout !== "vertical",
      "breakdance-control-wrapper-vertical":
        this.control.options.layout === "vertical",
      [`breakdance-control-wrapper-control-${this.control.options.type}`]: true
    };
  }

  get dynamicProperty() {
    return this.context.dynamicProperty(this.path);
  }

  get dynamicAccepts(): DynamicFieldReturnType | false {
    return this.dynamicProperty && this.dynamicProperty.accepts;
  }

  get pathWithBreakpoint() {
    return this.control.enableMediaQueries
      ? `${this.path}.${uiStore.currentBreakpoint.id}`
      : this.path;
  }

  get pathWithHoverAndBreakpoint() {
    return this.control.enableMediaQueries
      ? `${addHoverToPath(this.path)}.${uiStore.currentBreakpoint.id}`
      : addHoverToPath(this.path);
  }

  get fullPath() {
    return this.hoverEnabled
      ? this.pathWithHoverAndBreakpoint
      : this.pathWithBreakpoint;
  }

  get value() {
    return this.context.getControlValue(this.fullPath);
  }

  get aiEnabled() {
    if (!configStore.ai?.enabled) {
      return false;
    }

    return (
      (["text", "richtext"].includes(this.control.options.type) &&
        this.path.startsWith("content")) ||
      this.control.slug === "css"
    );
  }

  get hoverValuePresent() {
    const value = this.context.getControlValue(this.pathWithHoverAndBreakpoint);
    return !isControlValueEmpty(value);
  }

  hoverEnabled = false;
  toggleHover() {
    this.hoverEnabled = !this.hoverEnabled;
  }

  get showModifiers() {
    return this.hoverEnabled || !this.isBaseBreakpoint;
  }

  get isBaseBreakpoint() {
    return uiStore.currentBreakpoint.id === BASE_BREAKPOINT_ID;
  }

  get dynamicFieldMeta() {
    return this.context.getControlValue(
      getDynamicMetaPath(this.fullPath)
    ) as DynamicFieldOutput | null;
  }

  dynamicDataChosen(dynamicField: DynamicFieldOutput) {
    this.context.setControlValue(
      this.fullPath,
      dynamicField.shortcode,
      this.control
    );
    this.context.setControlValue(
      getDynamicMetaPath(this.fullPath),
      dynamicField,
      this.control
    );
  }

  insertCompletion(completion: string) {
    this.context.setControlValue(this.fullPath, completion, this.control);
  }

  mounted() {
    this.migrateNonBreakpointValue();
  }

  /*
   * Elements developers may need to enable media queries for existing controls.
   * By doing so, values are not migrated to the new format, showing the control input as empty.
   * The logic below will migrate the old value to the new format and wrap the value in a breakpoint object.
   */

  isResponsiveValue(val: Record<string, unknown>) {
    const ids = configStore.breakpointIds;
    return Object.keys(val).some(key => ids.includes(key));
  }

  shouldMigrateValue(val: unknown) {
    if (!this.control.enableMediaQueries) return false;

    if (isObjectExcludingArray(val)) {
      return !this.isResponsiveValue(val);
    }

    return typeof val === "string" || typeof val === "number";
  }

  migrateNonBreakpointValue() {
    const rawVal = this.context.getControlValue(this.path);

    if (this.shouldMigrateValue(rawVal)) {
      const migratedValue = { [BASE_BREAKPOINT_ID]: rawVal };
      console.log(
        `Migrating value for ${this.path} to new breakpoint format:`,
        migratedValue
      );

      breakdanceUndo.ignore(() => {
        this.context.setControlValue(this.path, migratedValue, this.control);
      });
    }
  }
}
